import React from "react"

import { registerNotificationFn } from "@/utils/notification-bridge"

import { Alert, Snackbar } from "@mui/material"
import type { SnackbarCloseReason } from "@mui/material"

export interface Notification {
  duration?: number | null
  message: string
  type: "success" | "error" | "info" | "warning"
}

interface NotificationContextValue {
  setNotification: (config: Notification) => void
}

interface NotificationProviderProps {
  children: React.ReactNode
}

const NotificationContext = React.createContext<NotificationContextValue>({
  setNotification: () => {
    console.error("setNotification not implemented")
  },
})

/**
 * Provides the notification service
 *
 * @example
 * <NotificationProvider>
 *   <Component />
 * </NotificationProvider>
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notification, setNotification] = React.useState<Notification | null>(
    null
  )
  const [isOpen, setIsOpen] = React.useState(false)

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }

    setIsOpen(false)
  }

  // Public notification setter to handle additional behavior
  const setNotificationPublic = React.useCallback(
    (newNotification: Notification): void => {
      setNotification(newNotification)
      setIsOpen(true)
    },
    [setNotification]
  )

  const value: NotificationContextValue = React.useMemo(
    () => ({
      setNotification: setNotificationPublic,
    }),
    [setNotificationPublic]
  )

  React.useEffect(() => {
    registerNotificationFn(setNotificationPublic)

    return () => {
      registerNotificationFn(null)
    }
  }, [setNotificationPublic])

  return (
    <NotificationContext value={value}>
      {children}
      <Snackbar
        autoHideDuration={
          notification?.duration === undefined ? 15_000 : notification.duration
        }
        key={notification?.message}
        onClose={handleClose}
        open={isOpen}
      >
        <Alert
          onClose={handleClose}
          severity={notification?.type ?? "info"}
          sx={{
            boxShadow: 4,
            width: "100%",
          }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </NotificationContext>
  )
}

/**
 * A service for creating notifications
 *
 * @example
 * const { setNotification } = useNotification()
 * setNotification({
 *   message: "Test notification",
 *   type: "success"
 * })
 */
export const useNotification = () => React.use(NotificationContext)
